<template>
  <div id="storeLocationDetails">
    <div id="storeName">
      {{ store.name }}
    </div>

    <div class="detailsContainer">
      <div class="storeDetails">
        <a style="margin: 4px 0"v-if="!!store.address || !!store.city" @click="openGoogleMap(store)">
          <span class="field">{{ $vuetify.t('$vuetify.common.address') }}:</span> {{ store.address }}
          <span v-if="store.city">, {{ store.city }} </span>
        </a>

        <a style="margin: 4px 0" v-if="!!store.phoneNumber" :href="`tel:${store.phoneNumber}`" @click="">
          <span class="field">{{ $vuetify.t('$vuetify.common.phoneNumber') }}:</span> {{ store.phoneNumber }}
        </a>

        <!-- <div style="margin: 20px auto 10px;">
          <open-close-chip :store="store" />
        </div> -->

        <!-- <div v-if="openUntil" id="openUntilDetails">
          <span class="field">Store Open:</span> {{ openUntil }}
          <span v-if="isMobile"> {{ store.phoneNumber }}</span>
        </div> -->
        <div id="openUntilDetails" style="padding-bottom: 8px">
          <div class="store-status">
            <span class="field">{{ $vuetify.t('$vuetify.common.storeHours') }}:</span>
            <span class="open-until">
              <template v-if="isHoliday">
                {{ todayHours.open }} - {{ todayHours.close }}
                <span class="closed-text">
                  ({{ $vuetify.t('$vuetify.common.storeHoursClosedHoliday') }})
                </span>
              </template>
              <template v-else-if="isStoreClosed">
                {{ todayHours.open }} - {{ todayHours.close }}
                <span class="closed-text">({{ $vuetify.t('$vuetify.common.closed') }})</span>
              </template>
              <template v-else-if="todayHours.open === 'Closed'">
                <span class="closed-text">({{ $vuetify.t('$vuetify.common.closed') }})</span>
              </template>
              <template v-else>
                {{ todayHours.open }} - {{ todayHours.close }}
              </template>
            </span>
          </div>
          <div v-if="onlineOrderUntil" style="margin: 4px 0">
            <span class="field">{{ $vuetify.t('$vuetify.common.onlineOrderAvailableUntil') }}:</span> {{ onlineOrderUntil }}
          </div>
        </div>
        <hr v-if="!isMobile"/>
      </div>

    <div :class="['orderTypeContainer', { 'custom-header-container': shouldUseCustomHeader }]">
    <hr class="tablet-delete" v-if="isMobile"/>
    <div class="order-container">
      <order-type-selector v-if="!(isDineInOnlyRoute || hasTableId)" :store="store" />

      <!-- Dine-in shared table UI — pixel-matches pickup toggle + info row -->
      <template v-if="(isDineInOnlyRoute || hasTableId) && isDineInSharedCartEnabled">
        <!-- Row 1: mode pill — same shape/size as pickup toggle-switch -->
        <div class="sc-toggle-container">
          <div class="sc-toggle-switch">
            <span class="sc-toggle-active">{{ $vuetify.t('$vuetify.orderTypes.dineIn') }}</span>
          </div>
        </div>

        <!-- Row 2: info row — same as pickup-details -->
        <div class="sc-pickup-details">
          <div class="sc-pickup-address">
            <strong>{{ $vuetify.t('$vuetify.orderTypes.dineIn') }}</strong>
            <span style="margin-left:5px;">{{ $vuetify.t('$vuetify.sharedCart.tableLabel', [getTableDisplay || getTableId]) }}</span>
          </div>
          <div class="sc-pickup-time">
            <strong v-if="!isSharedCartActive">{{ $vuetify.t('$vuetify.sharedCart.sharedTable') }}</strong>
            <strong v-else style="color:#34c759;">{{ $vuetify.t('$vuetify.sharedCart.sharedTableActive') }}</strong>
          </div>
        </div>
      </template>
    </div>
    <hr class="tablet-delete" v-if="isMobile"/>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex';
import { getStoreHoursMassaged, getStoreOpenAndClose, isBeforeOpen, formatTime, revertTimeFraction, getDisplayLocale, nowInStoreTz } from "@/services/util.service"

import OpenCloseChip from "@/components/OpenCloseChip"
import PickupDetailsCard from '@/components/Checkout/PickupDetailsCard'
import PickupDeliveryEditDialog from './Dialogs/PickupDeliveryEditDialog.vue';
import OrderTypeSelector from './OrderTypeSelector.vue';


export default {
   data() {
    return {
      newInput: false,
      showDeliveryPickup: true,
      scStartLoading: false,
    };
  },
  props: {
    store: {
      type: Object,
      required: true
    }
  },

  components: { OpenCloseChip, PickupDetailsCard, PickupDeliveryEditDialog, OrderTypeSelector },
  mounted() {
    const dineInOnly = this.$route.query.dineInOnly;

    if (dineInOnly) {
      this.showDeliveryPickup = false;
    }

    // Scanning a table's regular QR code IS joining that table's shared
    // cart -- always automatic, never a manual opt-in. Gated on dine-in
    // route + table id + the store setting being on; startSharedCart itself
    // is idempotent (scStartLoading guard), and getOrCreate joins the
    // table's already-active session if one exists, or creates one.
    if ((this.isDineInOnlyRoute || this.hasTableId) && this.isDineInSharedCartEnabled && !this.isSharedCartActive) {
      this.startSharedCart();
    }
  },
  computed: {
    getStoreHours() {
      return getStoreHoursMassaged(this.store)
    },
    todayHours() {
      const storeTz = (this.store && (this.store.timeZone || this.store.timezone)) || null;
      const today = nowInStoreTz(storeTz).getDay();
      const hours = this.getStoreHours[today];
      return hours || null;
    },
    isDineInOnlyRoute() {
      return !!this.$route.query.dineInOnly;
    },

    hasTableId() {
      return !!this.$route.query.tid;
    },
    
    isMobile() {
      const userAgent = window.navigator.userAgent.toLowerCase();
      const isTablet = /ipad|tablet|(android(?!.*mobile))/.test(userAgent);
      const isMobileDevice = /mobile|android|iphone|ipod/.test(userAgent) && !isTablet;
      const isSmallScreen = window.innerWidth <= 768;

      return this.$vuetify.breakpoint.smAndDown || isMobileDevice || isSmallScreen;
    },
    hasScheduledDateTime() {
      if (!this.getCheckoutInfo) return false;
      const scheduledTime = this.getCheckoutInfo.scheduledTime;
      const scheduledDay = this.getCheckoutInfo.scheduledDay;
      if (!scheduledTime || !scheduledDay) return false;
      if (scheduledTime === 'now') return true;
      return !!(this.formattedScheduledDay && this.formattedScheduledTime);
    },
    formattedScheduledDay() {
      if (!this.getCheckoutInfo.scheduledDay) return '';
      const raw = this.getCheckoutInfo.scheduledDay;
      if (raw === 'now') return '';
      const d = new Date(raw);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleDateString(getDisplayLocale(this.$vuetify.lang.current), { month: 'short', day: 'numeric' });
    },

    formattedScheduledTime() {
      if (!this.getCheckoutInfo.scheduledTime) return '';
      if (this.getCheckoutInfo.scheduledTime === 'now') return this.$vuetify.t('$vuetify.groupOrder.now');
      if (this.getCheckoutInfo.scheduledTime.id === 'now') return this.$vuetify.t('$vuetify.groupOrder.now');
      const d = new Date(this.getCheckoutInfo.scheduledTime);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleTimeString(getDisplayLocale(this.$vuetify.lang.current), { hour: 'numeric', minute: 'numeric', hour12: true });
    },
    formattedAddress() {
      if (!this.getDeliveryAddress) return 'Click to set';

      const { addressLine1, city, province, country } = this.getDeliveryAddress;

      if (!addressLine1 || !city || !province || !country) return 'Click to set';

      const fullAddress = `${addressLine1}, ${city}, ${province}, ${country}`;
      const truncatedAddress = this.isMobile ? this.truncate(fullAddress, 15) : this.truncate(fullAddress, 30);

      return this.$vuetify.t(truncatedAddress);
    },
    selectedOption: {
      get() {
        return this.getOrderType
      },
      set(val) {
        this.setOrderType(val)
      }
    },
    selectedIndex() {
      if (isNullOrEmpty(this.getStateOrderTypes))
        return 0
      return this.getStateOrderTypes.findIndex(_item => _item.value == this.selectedOption)
    },

    dineInExistingOrder() {
      return this.getExistingOrder.length && this.isDineIn || this.getDineInOnly
    },
    openUntil() {
      const dt = new Date()
      dt.setHours(0)
      dt.setMinutes(0)
      dt.setSeconds(0)
      let lang = this.$vuetify.lang.current
      let open = ''
      let until = ''
      let at = ''
      let extraText = ''
      let isTomorrow = false;
      switch(lang){
        case 'en': 
          until = 'Until';
          open = 'Open';
          at = 'at'
          extraText = 'tomorrow'
          break;
        case 'fr': 
          until = 'jusqu\'à';
          open = 'Ouvre';
          at = 'à'
          extraText = 'demain'
          break;
        case 'es': 
          until = 'Hasta';
          open = 'Abierto';
          at = 'a'
          extraText = 'mañana'
          break;
        case 'ko': 
          until = '까지';
          open = '열림';
          at = '에'
          extraText = '내일'
          break;
        case 'zh':
          until = '直到';
          open = '开放';
          at = '在'
          extraText = '明天'
          break;
        case 'zh-TW':
          until = '直到'
          open = '开放'
          at = '在';
          extraText = '明天';
          break;
        case 'th':
          until = 'จนถึง';
          open = 'เปิด';
          at = 'ที่';
          extraText = 'พรุ่งนี้';
          break;
        case 'ar':
          until = 'حتى';
          open = 'مفتوح';
          at = 'في'
          extraText = 'غداً'
          break;
        case 'vi':
          until = 'Đến';
          open = 'Mở';
          at = 'vào lúc'
          extraText = 'ngày mai'
          break;
        default: 
          until = 'Until';
          open = 'Open';
          at = 'at'
          extraText = 'tomorrow'
      }

      if (this.testStore(this.store)) {
        const todaysHours = getStoreHoursMassaged(this.store)[dt.getDay()]
        return !!todaysHours && todaysHours.close ? `${open} ${until} ${todaysHours.close}` : null
      } else {
        // When past todays open, grab tomorrows opening hours
        if (!isBeforeOpen(this.store)) {
          dt.setDate(dt.getDate() + 1)
          isTomorrow = true
        }
        const nextHours = getStoreHoursMassaged(this.store)[dt.getDay()]
        const { open: openTomorrow } = getStoreOpenAndClose(this.store, dt)
        if (openTomorrow !== undefined) {
          // Just show 12am here instead of "Open 24 hours"
          const openText = !extraText || openTomorrow !== 0 ? nextHours.open : '12am'
          if (isTomorrow) {
            return !!nextHours && nextHours.open ? `${open}s ${extraText} ${at} ${openText}` : null
          } else {
            return !!nextHours && nextHours.open ? `${open}s ${at} ${openText}` : null
          }
  
        } else {
          return null
        }
      }
    },

    onlineOrderUntil() {
      const storeTz = (this.store && (this.store.timeZone || this.store.timezone)) || null;
      const now = nowInStoreTz(storeTz)
      const dt = new Date(now)
      dt.setHours(0)
      dt.setMinutes(0)
      dt.setSeconds(0)

      if (this.testStore(this.store)) {
        const yesterday = new Date(dt)
        yesterday.setDate(yesterday.getDate() - 1)

        // We're only still inside YESTERDAY's overnight hours (e.g. it's 1am Tuesday
        // and Monday closes at 3am) when testYesterday() says so — not whenever
        // yesterday happens to have a past-midnight close time, which is true most days.
        const lookupDt = this.testYesterday(now, this.store) ? yesterday : dt
        const { close: orderClose } = getStoreOpenAndClose(this.store, lookupDt)
        const orderCloseFormatted = orderClose !== undefined ? formatTime(revertTimeFraction(orderClose)) : null
        return orderCloseFormatted ? orderCloseFormatted.replace(/(am|pm)/i, ' $1').toUpperCase() : null
      } else {
        if (isBeforeOpen(this.store)) {
          const { close: orderClose } = getStoreOpenAndClose(this.store, dt)
          const orderCloseFormatted = orderClose !== undefined ? formatTime(revertTimeFraction(orderClose)) : null
          return orderCloseFormatted ? orderCloseFormatted.replace(/(am|pm)/i, ' $1').toUpperCase() : null
        }
        return null
      }
    },

    ...mapGetters([
      'isHoliday',
      'shouldUseCustomHeader',
      'getCheckoutInfo',
      'getDeliveryAddress',
      'testStore',
      'testYesterday',
      'isDineIn',
      'isPickUp',
      'isStoreClosed',
      'testDateAgainstHours',
      'tillNextOpen',
      'getStore',
      'getOrderType',
      'getStoreOrderTypes',
      'getStateOrderTypes',
      'getExistingOrder',
      'isInKioskMode',
      'getTableId',
      'getTableDisplay',
      'getEnteredViaQr',
      'getDineInOnly' ]),
    isSharedCartActive() {
      return this.$store.getters['sharedCart/isActive']
    },
    isDineInSharedCartEnabled() {
      // Shared cart also requires the store to actually be on POS (posEnabled) --
      // the shared-cart session lives/settles through POS, so with POS off there's
      // nothing on the other end to receive it, regardless of the store's own
      // enableDineInSharedCart preference.
      return !!(this.getStore && this.getStore.posEnabled && this.getStore.settings && this.getStore.settings.owaSettings && this.getStore.settings.owaSettings.enableDineInSharedCart)
    },
  },

  methods: {
    getOrderTypeTranslation(type) {
      const knownTypes = ['DELIVERY', 'PICKUP'];
      return knownTypes.includes(type) 
        ? this.$vuetify.t(`$vuetify.orderTypes.${type.toLowerCase()}`)
        : type;
    },
    truncate(text, length) {
      if (!text) return '';
      return text.length > length ? text.substring(0, length) + '...' : text;
    },
    openGoogleMap(item) {
      const urlSuffix = item["address"] + ", " + item["city"] + ", " + item["postalCode"]

      window.open("https://www.google.com/maps/search/?api=1&query="+urlSuffix, "_blank")
    },
    async startSharedCart() {
      if (this.scStartLoading) return
      this.scStartLoading = true
      try {
        const storeId = this.getStore && this.getStore.storeId
        const tableId = this.getTableId
        // Dynamic QR code tables carry a signed, time-limited ?vc= token the backend
        // validates on the session's first fire/finalize -- must be captured now, at
        // session creation, or it's silently missing for the whole session's lifetime.
        const vc = this.$route.query.vc
        await this.$store.dispatch('sharedCart/startSession', { storeId, tableId, vc })
      } catch (e) {
        const msg = (e && e.response && e.response.data && e.response.data.errorMsg) || this.$vuetify.t('$vuetify.sharedCart.errorStartSharedTable')
        this.$store.dispatch('showSnack', { text: msg, color: 'error', timeout: 4000 })
      } finally {
        this.scStartLoading = false
      }
    },
    ...mapActions([ 'setOrderType', 'showSnack', 'setDeliveryAddress' ]),
    ...mapMutations([ 'SET_PICKUP_DELIVERY_EDIT_DIALOG', 'SET_DELIVERY_DIALOG', 'SET_CALL_SERVER_DIALOG', 'SET_CHECKOUT_INFO', 'SET_DELIVERY_PROVIDER', 'SET_DELIVERY_PROVIDERS_DETAILS' ])
  }
}
</script>

<style scoped>
  .orderTypeContainer {
    width: 66vw; 
  }
  
  .custom-header-container {
    width: calc(66vw - 100px) !important;
    margin-right: 7vw;
  }

  #openUntilDetails {
    display: flex;
    flex-direction: column;
    margin: 4px 0;
    width: 100%;
  }

  .store-status {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .phone-number {
    text-align: right;
  }
  .address {
    margin-left: 5px;
  }
  .detailsContainer {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: end;
    padding-bottom: 10px;
  }
  .storeDetails{
    padding-top: 18px;
    width: 33vw;
    text-align: start;
    margin-right: 100px;
  }
  #storeName {
    font-size: calc(var(--text-size-3) * 0.85) !important;
    font-weight: 700;
    text-align: start;
  }
  #storeLocationDetails, #openUntilDetails {
    font-size: calc(var(--text-size-1) * 1.2);
    /* font-weight: 600; */
  }
  #storeLocationDetails {
    margin-left: 18px;
  }
  #openUntilDetails {
    font-weight: 500;
  }

/* ────────────────────────────────────────────────────────────── */

/* ── Shared Table — exact match of pickup toggle + info row ──── */
.sc-toggle-container {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  width: 100%;
  max-width: 560px;
  margin-left: auto;
  margin-right: auto;
}

.sc-toggle-switch {
  display: flex;
  background: #ebebeb;
  border-radius: 50px;
  padding: 3px;
  flex: 1;
}

.sc-toggle-active {
  flex: 1;
  background: var(--v-primary-base);
  color: #fff;
  font-weight: 600;
  font-size: 14px;
  border-radius: 50px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px rgba(0,0,0,0.18);
}

.sc-pickup-details {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  font-weight: bold;
  padding: 6px 0;
  width: 100%;
}

.sc-pickup-address {
  flex: 1;
  text-align: right;
  white-space: nowrap;
  margin-right: 10px;
  color: var(--v-primary-base);
}

.sc-pickup-address strong {
  color: var(--v-primary-base);
}

.sc-pickup-time {
  flex: 1;
  text-align: left;
  white-space: nowrap;
  margin-left: 10px;
  display: flex;
  align-items: center;
}

.sc-pickup-time strong {
  color: var(--v-primary-base);
  margin-right: 4px;
}

@media only screen and (max-width: 920px) {
  .sc-toggle-container {
    max-width: 100%;
    padding: 0 15px;
    box-sizing: border-box;
  }
  .sc-pickup-details {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    padding: 4px 15px;
    box-sizing: border-box;
  }
  .sc-pickup-address {
    width: 100%;
    text-align: left;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-right: 0;
  }
  .sc-pickup-time {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-left: 0;
  }
}
/* ────────────────────────────────────────────────────────────── */

  #storeLocationDetails a {
    color: #2c3e50;
    display: block;
    text-decoration: none;
  }

  .title {
    font-size: 1.3rem;
    font-weight: bold;
    color: #2c3e50;
    margin-bottom: 15px;
  }

  .option-buttons {
    display: flex;
    justify-content: center;
    gap: 15px;
    margin-bottom: 15px;
  }

  .option-btn {
    padding: 10px 20px;
    font-size: 1.2rem;
    border: 2px solid #007bff;
    border-radius: 8px;
    background-color: white;
    color: #007bff;
    cursor: pointer;
    transition: all 0.3s ease;
  }

  .option-btn.active {
    background-color: #007bff;
    color: white;
  }

  .option-btn:hover {
    background-color: #0056b3;
    color: white;
  }

  .delivery-section {
    margin-top: 15px;
  }

  .address-input {
    width: 100%;
    padding: 10px;
    font-size: 1.1rem;
    border: 2px solid #ccc;
    border-radius: 6px;
    outline: none;
  }

  .address-input:focus {
    border-color: #007bff;
  }
  .order-container { 
    margin-right: 24px !important;
  }

  @media only screen and (max-width: 920px) {
    .custom-header-container {
      width: 100vw !important;
      margin-right: 0px;
    }
    .orderTypeContainer, .custom-header-container {
      width: 100vw;
      margin-right: 0;
    }
    .order-container { 
      margin-right: 0px !important;
    }
    .title {
      font-size: 1.3rem;
    }
    .option-btn {
      font-size: 1rem;
      padding: 8px 15px;
    }
    .address-input {
      font-size: 1rem;
    }
  }
 .field {
    color:  var(--v-primary-base);
    font-weight: 600;
  }
  #pickupDelivery {
  border-radius: 10px;
  padding: 16px;
  margin: auto;
  border: 1px solid #ddd;
}

/* Toggle Switch */
.toggle-container {
  display: flex;
  justify-content: center;
  margin-bottom: 10px;
}

.toggle-switch {
  display: flex;
  background: #f1f1f1;
  border-radius: 25px;
  padding: 5px;
  width: 60%;
}

.toggle-btn {
  flex: 1;
  border: none;
  padding: 5px 10px;
  border-radius: 25px;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.3s;
  background: transparent;
  color: #555;
}

.toggle-btn.active {
  background: var(--v-primary-base);
  color: white;
  font-weight: bold;
}

/* Info Section */
.info-section {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
}

.bold-text {
  font-weight: bold;
  color: var(--v-primary-base);
}

.store-address {
  text-decoration: none;
  color: black;
}

.pickup-details {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
  font-weight: bold;
  padding: 8px 0;
  width: 100%;
}

.closed-text {
  color: #b91c1c;
  font-weight: bold;
}

.pickup-address {
  flex: 1;
  text-align: right;
  text-decoration: none;
  white-space: nowrap;
  margin-right: 10px;
}

.pickup-address {
  text-decoration: none;
}

.pickup-address strong {
  color: var(--v-primary-base);
}

.pickup-address {
  color: #2c3e50;
}

.pickup-time strong {
  color: var(--v-primary-base);
}

.pickup-time {
  color: #2c3e50;
}

.pickup-time {
  flex: 1;
  text-align: left;
  cursor: pointer;
  white-space: nowrap;
  margin-left: 10px;
}

.pickup-time {
  text-decoration: none;
}
.dropdown-icon {
  font-size: 12px;
  margin-left: 5px;
  color: var(--v-primary-base);
}

/* Delivery Input */
.delivery-section {
  margin-top: 10px;
}

.delivery-address-input {
  width: 100%;
  padding: 8px;
  border: 1px solid #ccc;
  border-radius: 5px;
  font-size: 14px;
}
.delivery-address {
  margin-left: 5px;
  cursor: pointer;
  text-decoration: underline;
}

.delivery-address:hover {
  opacity: 0.8;
}

hr {
  border: none;
  height: 1px;
  background-color: #D9D9D9; 
  margin: 0;
}

@media only screen and (max-width: 920px) {
  #storeLocationDetails, #openUntilDetails {
    font-size: var(--text-size-1) !important;
  }
  #mainLogoImage {
    height: 20vh !important;
    margin-top: 5vh !important;
  }
  #openUntilDetails {
    margin: 4px 0;
  }
  .open-until {
    margin-left: 4px;
  }
  .store-status {
    flex: 1;
  }
  .pickup-address {
    width: 100%;
    display: flex !important;
    justify-content: space-between;
    align-items: center;
  }
  .pickup-details {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-start;
    font-size: 14px;
    font-weight: bold;
    padding: 8px 0;
    width: 100%;
    flex-wrap: wrap; 
    gap: 8px;
  }

  .pickup-time {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-left: 0;
  }

  .delivery-address {
    display: inline-block;
    max-width: 200px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: right;
  }
  .detailsContainer {
    padding-bottom: 0px;
  }
  .toggle-switch {
    width: 100%;
  }
  #pickupDelivery {
    margin-right: 0px;
    border: 0;
  }
  #storeName {
    text-align: center;
    font-size: calc(var(--text-size-3) * 0.65) !important;
  }
  #storeLocationDetails {
    margin-left: 0px;
  }
  .storeDetails {
    width: 100vw;
    padding: 0 18px;
  }

  .orderTypeContainer {
    width: 100vw;
    box-sizing: border-box;
    padding: 0 18px;
  }
  .orderTypeContainer > hr.tablet-delete:first-child {
    margin-bottom: 10px;
    margin-top: 10px;
  }

  .detailsContainer {
    flex-direction: column;
    align-items: flex-start;
  }
  .pickup-address strong,
  .pickup-time strong {
    margin-right: 8px;
    flex-shrink: 0;
  }

  .clickable {
    min-width: 80px; 
    text-align: right;
  }
}
@media only screen and (min-height: 600px) and (max-width: 1024px) {
  .tablet-delete {
    background-color: white !important;
  }
  #pickupDelivery {
    border-radius: 10px;
    padding: 16px;
    border: 1px solid #ddd;
  }
}
@media only screen and (max-width: 767px) {
  .tablet-delete {
    background-color: #D9D9D9 !important;
    display: block !important;
  }
  #pickupDelivery {
    border-radius: 10px;
    padding: 16px;
    margin-top: unset;
    border: 1px solid white;
  }
}
</style>
